import { Injectable, inject } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { Observable, combineLatest, map, startWith } from 'rxjs';
import { AppNotification } from '../models/notification';
import { NotificationService } from './notification.service';
import { UserService } from './user.service';

/** A single alert shown in the dropdown, from either source. */
export interface AppAlert {
  id: string;
  /** Material icon name. */
  icon: string;
  title: string;
  message: string;
  /**
   * Where clicking navigates. A UrlTree rather than a string because routes carry
   * a query param (`?meetingId=`), and RouterLink treats a bare string as one
   * path segment meaning it escapes the '?' instead of reading it as a query.
   */
  route: string | UrlTree;
  read: boolean;
  /** Derived alerts aren't dismissible — they clear by resolving their condition. */
  dismissible: boolean;
  /** Server-backed alerts only; absent on derived ones. */
  notificationId?: number;
  createdAt?: Date;
}

/**
 * Merges notifications stored server-side with alerts derived from local state
 * (currently just the pending privacy agreement, which has no row anywhere).
 * Components use this rather than NotificationService, so the difference between
 * the two kinds stays in one place.
 */
@Injectable({ providedIn: 'root' })
export class AlertsService {
  private userService = inject(UserService);
  private notificationService = inject(NotificationService);
  private router = inject(Router);

  /**
   * Whether the current user has not yet acknowledged the data privacy agreement.
   * @returns {boolean}
   */
  get privacyAgreementPending(): boolean {
    return !this.userService.currentUser?.agreementAgreedAt;
  }

  /** Derived alerts recompute when the user record changes, not only on fetch. */
  private userChanged$ = this.userService.userUpdated$.pipe(startWith(undefined));

  /** Everything the dropdown shows: derived alerts first, then notifications. */
  alerts$: Observable<AppAlert[]> = combineLatest([
    this.notificationService.notifications$,
    this.userChanged$,
  ]).pipe(
    map(([notifications]) => [
      ...this.derivedAlerts(),
      ...notifications.map((notification) => this.toAlert(notification)),
    ]),
  );

  /** Live arrivals in display shape — what the toast renders. */
  alertArrived$: Observable<AppAlert> = this.notificationService.arrived$.pipe(
    map((notification) => this.toAlert(notification)),
  );

  /**
   * Badge count. Derived alerts count as unread for as long as they apply, so a
   * pending privacy agreement keeps nagging after the dropdown is opened.
   */
  unreadCount$: Observable<number> = combineLatest([
    this.notificationService.unreadCount$,
    this.userChanged$,
  ]).pipe(map(([unread]) => unread + this.derivedAlerts().length));

  /** Whether older notifications remain unfetched. */
  hasMore$ = this.notificationService.hasMore$;

  /** Fetches server state and starts live delivery. Call once, on login. */
  load(): void {
    this.notificationService.start();
  }

  loadMore(): void {
    this.notificationService.loadMore().subscribe({ error: () => undefined });
  }

  /** Opening the dropdown clears the badge without emptying the list. */
  markAllRead(): void {
    this.notificationService.markAllRead().subscribe({ error: () => undefined });
  }

  markRead(alert: AppAlert): void {
    if (alert.notificationId === undefined) return;
    this.notificationService.markRead(alert.notificationId).subscribe({ error: () => undefined });
  }

  dismiss(alert: AppAlert): void {
    if (!alert.dismissible || alert.notificationId === undefined) return;
    this.notificationService.dismiss(alert.notificationId).subscribe({ error: () => undefined });
  }

  /**
   * Clears every dismissible alert at once. Derived (non-dismissible) alerts
   * like the pending privacy agreement aren't server notifications, so
   * there's nothing to dismiss and they're unaffected by this.
   */
  dismissAll(): void {
    this.notificationService.dismissAll().subscribe({ error: () => undefined });
  }

  /**
   * Presentation comes from the notification's own data snapshot, not a
   * client-side per-type map — the copy is written when the event happens, so a
   * new type needs no change here.
   * @param notification
   * @returns {AppAlert}
   */
  private toAlert(notification: AppNotification): AppAlert {
    return {
      id: `notification-${notification.id}`,
      icon: String(notification.data?.['icon'] ?? 'notifications'),
      title: String(notification.data?.['title'] ?? 'Notification'),
      message: String(notification.data?.['message'] ?? ''),
      route: this.router.parseUrl(String(notification.data?.['route'] ?? '/')),
      read: notification.isRead,
      dismissible: true,
      notificationId: notification.id,
      // displayTime (updatedAt), not createdAt: a collapsed notification's
      // createdAt stays frozen at the first event in the burst even as its
      // content keeps moving on to later ones — see AppNotification.
      createdAt: notification.displayTime,
    };
  }

  /** Alerts computed from local state rather than fetched. */
  private derivedAlerts(): AppAlert[] {
    if (!this.privacyAgreementPending) return [];

    return [
      {
        id: 'privacy-agreement',
        icon: 'shield',
        title: 'Privacy agreement pending',
        message: 'Please review and acknowledge the data privacy agreement.',
        route: '/privacy',
        read: false,
        dismissible: false,
      },
    ];
  }
}
